<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const UNAVAILABLE_ROUTES = {
  spaces: 'settings_spaces_index',
  experiences: 'planning_experiences_index',
  knowledgebase: 'settings_knowledgebases_index',
};

const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const templates = computed(
  () => store.state.assistantWizard.procedureTemplates
);

const name = computed(() => store.state.assistantWizard.name);
const selectedIds = computed(
  () => store.state.assistantWizard.procedureTemplateIds
);

function templateTitle(template) {
  const key = `ASSISTANT_MGMT.PROCEDURES.STANDARD.TEMPLATES.${template.id}`;
  const translated = t(key);
  return translated === key ? template.name : translated;
}

function isUnavailable(template) {
  return template.available === false;
}

function isSelected(template) {
  return selectedIds.value.includes(template.id);
}

function unavailableLabel(template) {
  const reason = template.unavailable_reason;
  if (!reason) return '';
  const key = `ASSISTANT_MGMT.PROCEDURES.STANDARD.UNAVAILABLE.${reason.toUpperCase()}`;
  const translated = t(key);
  return translated === key ? '' : translated;
}

function unavailableRoute(template) {
  const name = UNAVAILABLE_ROUTES[template.unavailable_reason];
  return name ? accountScopedRoute(name) : null;
}

function isUnavailableLink(template) {
  return isUnavailable(template) && Boolean(unavailableRoute(template));
}

function toggleTemplate(template) {
  if (isUnavailable(template)) return;
  store.dispatch('assistantWizard/toggleProcedureTemplateId', template.id);
}

const next = () => store.dispatch('assistantWizard/next');
</script>

<template>
  <section class="flex items-center flex-col w-full max-w-[800px]">
    <form
      class="flex flex-col w-full flex-1 mb-4 items-center"
      data-testid="assistant-capabilities"
      @submit.prevent="next()"
    >
      <label
        class="text-xl text-center text-neutral-900 dark:text-neutral-50 mb-8"
      >
        {{ $t('ASSISTANT_MGMT.WIZARD.CAPABILITIES_LABEL', { name: name }) }}
      </label>
      <div class="flex flex-col md:flex-row gap-4 w-full">
        <component
          :is="isUnavailableLink(template) ? 'router-link' : 'button'"
          v-for="template in templates"
          :key="template.id"
          :to="
            isUnavailableLink(template) ? unavailableRoute(template) : undefined
          "
          :type="isUnavailableLink(template) ? undefined : 'button'"
          class="box flex flex-col items-center w-full p-6 border-2 rounded-xl text-center cursor-pointer bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 hover:border-woot-600"
          :class="{
            active: isSelected(template),
            'opacity-60': isUnavailable(template),
          }"
          :data-testid="`wizard-capability-${template.id}`"
          @click="toggleTemplate(template)"
        >
          <div
            class="check-container bg-neutral-100 dark:bg-neutral-800 rounded-full text-white mb-2 p-2"
          >
            <fluent-icon icon="checkmark" />
          </div>
          <h3 class="mb-2 font-semibold text-lg">
            {{ templateTitle(template) }}
          </h3>
          <p class="leading-tight">{{ template.description }}</p>
          <span
            v-if="isUnavailable(template)"
            class="text-xs font-medium text-woot-700 underline mt-2"
          >
            {{ unavailableLabel(template) }}
          </span>
        </component>
      </div>
    </form>
  </section>
</template>

<style scoped lang="scss">
.box.active {
  border-color: #645298;
  background: #f0eef6;
}
.dark .box.active {
  border-color: #645298;
  background: #292932;
}
.box.active .check-container {
  background: #645298;
}
</style>
